// src/features/notifications/TemplateVariablesEditor.tsx
//
// Placeholder helper for notification templates: `{{variable}}` tokens used
// in the subject/body are detected and shown as chips so the required-
// variables list doesn't have to be typed blind as a comma-separated string.
// Clicking a detected chip toggles whether it's required; "+ add" both
// inserts `{{name}}` into the body at the cursor and marks it required.

import { useState, type RefObject } from 'react';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';
import { extractTemplatePlaceholders } from './logic';

export interface TemplateVariablesEditorProps {
  bodyRef: RefObject<HTMLTextAreaElement>;
  subjectTemplate: string;
  bodyTemplate: string;
  onBodyChange: (value: string) => void;
  requiredVariables: string[];
  onRequiredVariablesChange: (vars: string[]) => void;
}

export function TemplateVariablesEditor({
  bodyRef,
  subjectTemplate,
  bodyTemplate,
  onBodyChange,
  requiredVariables,
  onRequiredVariablesChange,
}: TemplateVariablesEditorProps) {
  const { t } = useI18n();
  const [newVar, setNewVar] = useState('');

  const detected = extractTemplatePlaceholders(subjectTemplate, bodyTemplate);
  // Required variables no longer present in the text still show (dashed) so they can be removed.
  const stale = requiredVariables.filter((v) => !detected.includes(v));
  const chips = [...detected, ...stale];

  const toggleRequired = (name: string) => {
    onRequiredVariablesChange(
      requiredVariables.includes(name)
        ? requiredVariables.filter((v) => v !== name)
        : [...requiredVariables, name]
    );
  };

  const insertIntoBody = (name: string) => {
    const token = `{{${name}}}`;
    const el = bodyRef.current;
    if (!el) {
      onBodyChange(bodyTemplate ? `${bodyTemplate} ${token}` : token);
      return;
    }
    const start = el.selectionStart ?? bodyTemplate.length;
    const end = el.selectionEnd ?? bodyTemplate.length;
    const next = bodyTemplate.slice(0, start) + token + bodyTemplate.slice(end);
    onBodyChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const addVariable = () => {
    const name = newVar.trim().replace(/\s+/g, '_');
    if (!name) return;
    insertIntoBody(name);
    if (!requiredVariables.includes(name)) onRequiredVariablesChange([...requiredVariables, name]);
    setNewVar('');
  };

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">{t('notifications.templateVariables.hint')}</p>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((name) => {
            const required = requiredVariables.includes(name);
            const isStale = stale.includes(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggleRequired(name)}
                title={
                  isStale
                    ? t('notifications.templateVariables.staleHint')
                    : t('notifications.templateVariables.toggleHint')
                }
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-xs transition-colors',
                  required
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-dashed border-border text-muted-foreground hover:text-foreground'
                )}
              >
                {`{{${name}}}`}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input
          value={newVar}
          onChange={(e) => setNewVar(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addVariable();
            }
          }}
          placeholder={t('notifications.templateVariables.addPlaceholder')}
          autoCapitalize="off"
          className="w-40 rounded-md border border-dashed border-input bg-transparent px-2 py-1 font-mono text-xs text-foreground placeholder:font-sans focus:outline-none"
        />
        <button
          type="button"
          onClick={addVariable}
          disabled={!newVar.trim()}
          className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
        >
          {t('notifications.templateVariables.insert')}
        </button>
      </div>
    </div>
  );
}

export default TemplateVariablesEditor;
